import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  CircularProgress,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  Typography
} from '@mui/material'

import { cloneDeep, get, isEmpty, isEqual, omit } from 'lodash'
import {
  useEffect,
  useState,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  unstable_useBlocker as useBlocker,
  useNavigate
} from 'react-router-dom'

import { ADMIN_CONTACT_EMAIL, ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { isAuthorized, useMobileBreakpoints } from 'utils'

import {
  AlertBanner,
  AlertDialog,
  FormLabel,
  MButton,
  UnsavedChangesDialog,
  VisuallyHiddenInput
} from 'components'
import { useAuth } from 'context'
import { EditAssetCarousel } from '../../Assets/components'
import { CampaignAccordions } from './CampaignAccordions'

const INIT_MSG_FORM_DATA = {
  campaignName: '',
  throttleOption: '0',
  text: {
    isActive: false,
    file: null,
    template: ''
  },
  email: {
    isActive: false,
    file: null,
    htmlTemplate: '',
    plainTextTemplate: '',
    subject: ''
  },
  voice: {
    isActive: false,
    file: null,
    template: ''
  }
}

const THROTTLE_OPTION = [
  { label: 'All at once', value: '0' },
  { label: 'Over 1 hour', value: '1' },
  { label: 'Over 2 hour', value: '2' },
  { label: 'Over 3 hour', value: '3' },
  { label: 'Over 4 hour', value: '4' },
  { label: 'Over 6 hour', value: '6' },
  { label: 'Over 8 hour', value: '8' }
]

interface ICreateNewCampaign {
  campaignId: string
  setActiveTab: Dispatch<SetStateAction<number>>
}

export const CreateNewCampaign = (props: ICreateNewCampaign): JSX.Element => {
  const isMobile = useMobileBreakpoints()
  const navigate = useNavigate()

  const {
    uploadAssets,
    getCampaignById,
    createCampaign,
    editCampaign,
    sendCampaign,
    handleLogOut
  } = useAuth()

  const [dialogData, setDialogData] = useState<any>({
    type: '',
    isOpen: false,
    title: '',
    message: ''
  })

  const [initFormData, setInitFormData] = useState<any>({})
  const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [origCampaignName, setOrigCampaignName] = useState<string>('')

  const [editCarouselData, setEditCarouselData] = useState<any>({
    isOpen: false,
    failedFiles: [],
    fileData: {}
  })

  const handleCloseCarousel = async (): Promise<void> => {
    setEditCarouselData({ ...editCarouselData, isOpen: false })
  }

  const { handleSubmit, control, setValue, watch, getValues, formState } =
    useForm({
      mode: 'onChange',
      defaultValues: INIT_MSG_FORM_DATA
    })

  const hasOneActiveChannel =
    watch('text.isActive') ||
    watch('email.isActive') ||
    watch('voice.isActive')

  const hasFileAttachedOnActiveChannel =
    (watch('text.isActive') && !isEmpty(get(watch('text.file'), 'name', ''))) ||
    (watch('email.isActive') &&
      !isEmpty(get(watch('email.file'), 'name', ''))) ||
    (watch('voice.isActive') && !isEmpty(get(watch('voice.file'), 'name', '')))

  const isFormChanged = (): boolean => {
    const initFormDataWithoutFile = omit(cloneDeep(initFormData), [
      'text.file',
      'email.file',
      'voice.file'
    ])
    const formDataWithoutFile = omit(cloneDeep(watch()), [
      'text.file',
      'email.file',
      'voice.file'
    ])

    return !isEqual(initFormDataWithoutFile, formDataWithoutFile)
  }

  const isValidToSend = (): boolean => {
    if (!hasOneActiveChannel || !hasFileAttachedOnActiveChannel) {
      return false
    }
    return true
  }

  const getCampaignPayload = (data: any): any => {
    return {
      name: data.campaignName,
      smsTemplate: data.text.isActive ? data.text.template : '',
      htmlTemplate: data.email.isActive ? data.email.htmlTemplate : '',
      emailSubject: data.email.subject,
      plainTextTemplate: data.email.isActive
        ? data.email.plainTextTemplate
        : '',
      ivrTemplate: data.voice.isActive ? data.voice.template : '',
      throttle: data.throttleOption
    }
  }

  const initCampaign = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const { campaign } = await getCampaignById(props.campaignId)

      setOrigCampaignName(campaign.name)
      setValue('campaignName', campaign.name)
      setValue('throttleOption', campaign?.throttle ?? '0')

      setValue('text.template', campaign.smsTemplate)
      setValue('voice.template', campaign.ivrTemplate)
      setValue('email.htmlTemplate', campaign.htmlTemplate)
      setValue('email.plainTextTemplate', campaign.plainTextTemplate)
      setValue('email.subject', campaign.emailSubject)

      setValue('text.isActive', !isEmpty(campaign.smsTemplate))
      setValue(
        'email.isActive',
        !isEmpty(campaign.htmlTemplate) || !isEmpty(campaign.plainTextTemplate)
      )
      setValue('voice.isActive', !isEmpty(campaign.ivrTemplate))

      setInitFormData(cloneDeep(getValues()))
      setIsLoading(false)
    } catch (error) {
      if (!isAuthorized(error)) await handleLogOut()
      navigate(ROUTES_PATH.CAMPAIGNS)
      props.setActiveTab(0)
      setIsLoading(false)
    }
  }

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      !isEqual(initFormData, watch()) &&
      currentLocation.pathname !== nextLocation.pathname &&
      nextLocation.pathname !== ROUTES_PATH.CAMPAIGN_LOGS
    )
  })

  useEffect(() => {
    if (props.campaignId !== '') {
      void initCampaign()
    } else {
      setInitFormData(INIT_MSG_FORM_DATA)
    }
  }, [])

  const onSubmit = async (data: any): Promise<void> => {
    const campaignPayload = getCampaignPayload(data)

    const formData = new FormData()

    formData.append('emailCsv', data.email.file)
    formData.append('ivrCsv', data.voice.file)
    formData.append('smsCsv', data.text.file)

    if (!props.campaignId) {
      for (const key in campaignPayload) {
        formData.append(key, campaignPayload[key])
      }
    } else if (props.campaignId !== '') {
      formData.append('campaignId', props.campaignId)
    }

    try {
      await handleSave(true)

      await sendCampaign(formData)

      setDialogData({
        type: 'success',
        isOpen: true,
        title: 'Campaign Sent',
        message:
          'You have successfully sent and saved a campaign. You can find and reuse this campaign under Campaigns'
      })
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()

      setDialogData({
        type: 'error',
        isOpen: true,
        title: 'An error occured',
        message: `We are really sorry but there was a problem and we couldn’t save and send your campaign. Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
      })
    }
  }

  const handleSave = async (
    throwError: boolean,
    savedAsNew: boolean = false
  ): Promise<void> => {
    const formValues = getValues()
    const campaignPayload = getCampaignPayload(formValues)

    try {
      if (props.campaignId !== '' && !savedAsNew) {
        await editCampaign(props.campaignId, campaignPayload)
        setDialogData({
          type: 'success',
          isOpen: true && !throwError,
          title: 'Success! Your Campaign Has Been Saved',
          message:
            'You can either stay on this page to send the message and keep making changes, or close this window to return to the homepage.'
        })
      } else {
        const response = await createCampaign(campaignPayload)
        navigate(`${ROUTES_PATH.CAMPAIGNS}?id=${response.campaign._id}`)

        setDialogData({
          type: 'success',
          isOpen: true && !throwError,
          title: savedAsNew
            ? 'Success! Your Campaign Has Been Saved as New'
            : 'Success! Your Campaign Has Been Saved',
          message:
            'You can either stay on this page to send the message and keep making changes, or close this window to return to the homepage.'
        })
      }
      setInitFormData(getValues())
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()

      if (
        get(error, 'response.data.error', '').includes(
          'duplicate key error collection'
        )
      ) {
        setIsDuplicateName(true)
      } else {
        setDialogData({
          type: 'error',
          isOpen: !throwError,
          title: 'An error occured',
          message: `We are really sorry but there was a problem and we couldn’t save your campaign. Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>.`
        })
      }

      if (throwError) {
        throw new Error(`Failed to saved campaign due to: ${error.message}`)
      }
    }
  }

  const handleUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = event.target.files
    if (files != null) {
      try {
        const formData = new FormData()
        Array.from(files).forEach((file) => {
          formData.append('assets', file)
        })

        const response = await uploadAssets(formData)

        const failedFiles = response.result.filter(
          (item: any) =>
            item.status === 'FAILED' && item.error === 'Duplicate Asset Name'
        )

        if (isEmpty(failedFiles)) {
          setDialogData({
            type: 'success',
            isOpen: true,
            title: 'Assets have been uploaded',
            message:
              'You have successfully uploaded an asset and now its ready to be use in your campaigns.'
          })
        } else {
          setEditCarouselData({
            isOpen: true,
            failedFiles,
            fileData: files
          })
        }
      } catch (error: any) {
        if (!isAuthorized(error)) await handleLogOut()
        setDialogData({
          type: 'error',
          isOpen: true,
          title: 'Asset wasn’t uploaded',
          message: `Something went wrong and the asset was not uploaded. Please try again, if the error persists, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
        })
      }
    }
  }

  return (
    <>
      {isLoading
        ? (
          <Box
            p={2}
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="80vh"
          >
            <CircularProgress />
          </Box>
        )
        : (
          <>
            <Box
              display="flex"
              p={3}
              gap={1}
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              borderBottom={`solid 1px ${color.secondary.dark[200]}`}
            >
              <Box display="flex" gap={1}>
                <Typography
                  color={color.grey[900]}
                  fontWeight={400}
                  fontSize={24}
                >
                  {!isEmpty(props.campaignId) ? origCampaignName : 'New Campaign'}
                </Typography>

                {!isEmpty(props.campaignId) && (
                  <Box p={1} bgcolor={color.secondary.dark[50]}>
                    <Typography
                      color={color.secondary.dark[600]}
                      fontWeight={400}
                      fontSize={16}
                    >
                      Existing Campaign
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box display="flex" gap={1}>
                <MButton
                  variant="outlined"
                  component="label"
                  type="button"
                  rounded="true"
                  sx={{ fontSize: 16 }}
                  texttransform="none"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Asset
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    multiple
                  />
                </MButton>

                <MButton
                  variant="outlined"
                  type="button"
                  rounded="true"
                  texttransform="none"
                  onClick={() => {
                    navigate(ROUTES_PATH.CAMPAIGNS)
                    props.setActiveTab(0)
                  }}
                >
                  Go Back to Campaign Gallery
                </MButton>
              </Box>
            </Box>
            <form
              method="post"
              onSubmit={handleSubmit(async (data) => {
                await onSubmit(data)
              })}
            >
              <Box
                p={3}
                bgcolor={color.white}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Box width={isMobile ? '90%' : '60%'}>
                  <Controller
                    name="campaignName"
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({
                      field: { onChange, value, name }
                    }): JSX.Element => (
                      <Box display="flex" mt={3} flexDirection="column">
                        <FormLabel>Campaign Name</FormLabel>
                        <Input
                          name={name}
                          value={value}
                          onChange={(e) => {
                            setIsDuplicateName(false)
                            onChange(e)
                          }}
                          placeholder="Enter Campaign Name Here"
                          sx={{ fontSize: '1.5rem' }}
                          error={isDuplicateName}
                          endAdornment={
                            <InputAdornment position="end">
                              <EditIcon sx={{ color: color.primary[700] }} />
                            </InputAdornment>
                          }
                        />
                      </Box>
                    )}
                  />

                  <CampaignAccordions
                    setValue={setValue}
                    watch={watch}
                    control={control}
                  />

                  <Box
                    display="flex"
                    mt={3}
                    bgcolor={color.grey[50]}
                    p={2}
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Box display="flex" flexDirection="column">
                      <FormLabel>SAVE OPTIONS</FormLabel>
                      <Typography
                        color={color.grey[700]}
                        fontWeight={400}
                        fontSize={14}
                      >
                        If you want to save this file to be used later, please
                        save it.
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      <MButton
                        variant="outlined"
                        size="large"
                        rounded="true"
                        texttransform="none"
                        sx={{ fontSize: 16 }}
                        disabled={
                          !formState.isValid ||
                          !hasOneActiveChannel ||
                          !isFormChanged()
                        }
                        onClick={() => {
                          void handleSave(false)
                        }}
                      >
                        Save Campaign
                      </MButton>

                      {props.campaignId && (
                        <MButton
                          variant="outlined"
                          size="large"
                          rounded="true"
                          texttransform="none"
                          sx={{ fontSize: 16 }}
                          disabled={
                            !formState.isValid ||
                            !hasOneActiveChannel ||
                            isEqual(
                              initFormData.campaignName,
                              watch('campaignName')
                            )
                          }
                          onClick={() => {
                            void handleSave(false, true)
                          }}
                        >
                          Save As New
                        </MButton>
                      )}
                    </Box>
                  </Box>
                  <AlertBanner
                    type="warning"
                    message="* Make sure you have filled the <b>Active channels</b> and provided a <b>Campaign Name</b>."
                    isVisible={!formState.isValid}
                  />

                  <AlertBanner
                    type="warning"
                    message="* In order to save it as new  you need to provide a different name file ."
                    isVisible={
                      !isEmpty(props.campaignId) &&
                      isEqual(initFormData.campaignName, watch('campaignName'))
                    }
                  />

                  <AlertBanner
                    type="error"
                    message="* You must have a different campaign name in order to save it as a new one."
                    isVisible={isDuplicateName}
                  />

                  <Controller
                    name="throttleOption"
                    control={control}
                    render={({ field }): JSX.Element => (
                      <Box my={3}>
                        <Box
                          display="flex"
                          bgcolor={color.grey[50]}
                          p={2}
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Box display="flex" flexDirection="column">
                            <FormLabel>
                              How do you want to send this campaign?
                            </FormLabel>

                            <Box display="flex" flexWrap="wrap">
                              <Select
                                sx={{ minWidth: 130 }}
                                displayEmpty={true}
                                {...field}
                                disabled={!formState.isValid || !isValidToSend()}
                              >
                                {THROTTLE_OPTION.map((item, index) => (
                                  <MenuItem key={index} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                          </Box>

                          <MButton
                            variant="outlined"
                            type="submit"
                            size="large"
                            rounded="true"
                            texttransform="none"
                            sx={{ fontSize: 16 }}
                            disabled={!formState.isValid || !isValidToSend()}
                          >
                            Send Campaign
                          </MButton>
                        </Box>
                        <AlertBanner
                          type="warning"
                          message="* Please make sure you have save the campaign and added the CSV Files to your active channels."
                          isVisible={!formState.isValid || !isValidToSend()}
                        />
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </form>
            <AlertDialog
              open={dialogData.isOpen}
              type={dialogData.type}
              title={dialogData.title}
              message={dialogData.message}
              handleClick={() => {
                setDialogData({ ...dialogData, isOpen: false })
              }}
            >
              <Box display="flex" flexDirection="column" gap={1}>
                {(dialogData.title ===
                  'Success! Your Campaign Has Been Saved as New' ||
                  dialogData.title ===
                  'Success! Your Campaign Has Been Saved') && (
                    <MButton
                      variant="contained"
                      type="button"
                      rounded="true"
                      texttransform="none"
                      onClick={() => {
                        setDialogData({ ...dialogData, isOpen: false })
                        if (
                          dialogData.title ===
                          'Success! Your Campaign Has Been Saved as New'
                        ) {
                          navigate(0)
                        } else {
                          void initCampaign()
                        }
                      }}
                    >
                      Stay on this page
                    </MButton>
                  )}

                <MButton
                  variant="outlined"
                  type="button"
                  rounded="true"
                  texttransform="none"
                  onClick={() => {
                    navigate(ROUTES_PATH.CAMPAIGNS)
                    props.setActiveTab(0)
                  }}
                >
                  Go Back to Campaign Gallery
                </MButton>

                {dialogData.title === 'Campaign Sent' && (
                  <MButton
                    variant="contained"
                    type="button"
                    rounded="true"
                    texttransform="none"
                    onClick={() => {
                      navigate(
                        `${ROUTES_PATH.CAMPAIGN_LOGS}?id=${props.campaignId}`
                      )
                    }}
                  >
                    View Logs
                  </MButton>
                )}
              </Box>
            </AlertDialog>
            <UnsavedChangesDialog
              open={blocker.state === 'blocked'}
              onConfirm={blocker.proceed}
              onClose={blocker.reset}
              isLoading={false}
            />
            {editCarouselData.isOpen && (
              <EditAssetCarousel
                isOpen={editCarouselData.isOpen}
                onClose={handleCloseCarousel}
                failedFiles={editCarouselData.failedFiles}
                fileData={editCarouselData.fileData}
              />
            )}
          </>
        )}
    </>
  )
}
